import React, {ChangeEvent, useState} from "react";
import {DateObject} from "react-multi-date-picker";
import apiService from "../services/api-service";
import {AccommodationTypeDto, AdditionalServiceDto, FeeTypeDto, KCTEventDto,} from "../types/contracts";
import {useEventsContext} from "../providers/EventsProvider";
import {FormField} from "../types/enums";

export const useAdminEventConfigurator = () => {
  const [event, setEvent] = useState<KCTEventDto>();
  const [feeTypes, setFeeTypes] = useState<FeeTypeDto[]>([]); // New state for feeTypes
  const [services, setServices] = useState<AdditionalServiceDto[]>([]);
  const [accommodationTypes, setAccommodationTypes] = useState<
    AccommodationTypeDto[]
  >([]);
  const [submittedAccommodationTypes, setSubmittedAccommodationTypes] = useState<AccommodationTypeDto[]>([]);

  const { events } = useEventsContext()

  const loadSpecificEvent = (eventId: string) => {
    // Load specific event details for editing
    if (eventId !== "") {
      apiService
        .getEvent(eventId)
        .then((response) => {
          setEvent(response.data as KCTEventDto);
          setServices((response.data as KCTEventDto).additionalServices);
          setFeeTypes((response.data as KCTEventDto).feeTypes);
          setAccommodationTypes((response.data as KCTEventDto).accommodations);
          setSubmittedAccommodationTypes((response.data as KCTEventDto).accommodations);
        })
        .catch((error) => {
          console.error("Error loading event:", error);
        });
    } else {
      setEvent(undefined);
      setAccommodationTypes([]);
      setFeeTypes([]);
      setServices([]);
    }
  };

  const handleEventChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    console.log(name);
    if (name === "isCurrentEvent") {
      setEvent((prevEvent) => ({
        ...prevEvent!,
        [name]: !prevEvent?.isCurrentEvent,
      }));
    } else {
      setEvent((prevEvent) => ({ ...prevEvent!, [name]: value }));
    }
  };

  const handleDatesChange = (dates: Date[]) => {
    const formattedDates = dates.map((date: string | number | Date) => {
      const dateObject =
        date instanceof DateObject ? date.toDate() : new Date(date);
      const year = dateObject.getFullYear();
      const month = String(dateObject.getMonth() + 1).padStart(2, "0"); // getMonth is zero-based
      const day = String(dateObject.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    });

    setEvent((prevEvent) => ({ ...prevEvent!, dates: formattedDates }));
  };

  const handleServiceChange = (
    index: number,
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, type, value } = e.target;
    const updatedServices = [...services];
    let newValue: string | number | boolean;

    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      newValue = e.target.checked;
    } else if (type === "number") {
      newValue = Number(value);
    } else {
      newValue = value;
    }

    updatedServices[index] = {
      ...updatedServices[index],
      [name]: newValue,
    };

    setServices(updatedServices);
  };

  const handleCheckboxChange = (index: number) => {
    const updatedServices = [...services];
    updatedServices[index].forWholeEvent =
      !updatedServices[index].forWholeEvent;
    if (updatedServices[index].forWholeEvent) {
      updatedServices[index].date = null;
    }
    setServices(updatedServices);
  };

  const addService = () => {
    setServices([
      ...services,
      {
        name: "",
        amount: 0,
        description: "",
        date: "",
        forWholeEvent: false,
        id: "",
        isHiddenInRegistration: false,
      },
    ]);
  };

  const removeService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
  };
  const handleFeeTypeChange = (
    index: number,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const updatedFeeTypes = [...feeTypes];
    updatedFeeTypes[index] = { ...updatedFeeTypes[index], [name]: value };
    setFeeTypes(updatedFeeTypes);
  };

  const handleAccommodationTypeChange = (
      index: number,
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, type, value } = e.target;
    const updatedAccommodationTypes = [...accommodationTypes];

    let newValue: string | number | boolean;

    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      newValue = e.target.checked;
    } else if (type === "number") {
      newValue = Number(value);
    } else {
      newValue = value;
    }

    updatedAccommodationTypes[index] = {
      ...updatedAccommodationTypes[index],
      [name]: newValue,
    };

    setAccommodationTypes(updatedAccommodationTypes);
  };

  const handleFeeCheckboxChange = (index: number, name: string) => {
    const updatedFeeTypes = [...feeTypes];
    if (
      name === "requiresMembershipCardNumber" ||
      name === "allowsAdvancedInformation"
    ) {
      updatedFeeTypes[index][name] = !updatedFeeTypes[index][name];
    }
    setFeeTypes(updatedFeeTypes);
  };

  const addFeeType = () => {
    setFeeTypes([
      ...feeTypes,
      {
        name: "",
        amount: 0,
        requiresMembershipCardNumber: false,
        allowsAdvancedInformation: false,
        visibleFormFields: [],
        id: "",
        description: "",
      },
    ]);
  };

  const addAccommodationType = () => {
    setAccommodationTypes([
      ...accommodationTypes,
      {
        name: "",
        isSelfArrangedAccommodation: false,
        capacity: 0,
        price: 0,
        eventId: Number(event?.id) || 0,
        id: "",
        isPricePerNight: true,
        isHiddenInRegistration: false,
      },
    ]);
  };

  const removeFeeType = (index: number) => {
    setFeeTypes(feeTypes.filter((_, i) => i !== index));
  };

  const removeAccommodationType = (index: number) => {
    setAccommodationTypes(accommodationTypes.filter((_, i) => i !== index));
  };

  const handleFormFieldsChange = (index: number, field: FormField) => {
    const updatedFeeTypes = [...feeTypes];
    const currentFields = updatedFeeTypes[index].visibleFormFields || [];

    const isAlreadyVisible = currentFields.includes(field);

    if (isAlreadyVisible) {
      updatedFeeTypes[index].visibleFormFields = currentFields.filter((f) => f !== field);
    } else {
      updatedFeeTypes[index].visibleFormFields = [...currentFields, field];
    }

    setFeeTypes(updatedFeeTypes);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!event) {
      console.error("Event is undefined");
      alert("Událost není vytvořena");
      return;
    }

    event.additionalServices = services;
    event.feeTypes = feeTypes;
    event.accommodations = accommodationTypes;
    setEvent(event);
    setSubmittedAccommodationTypes([...accommodationTypes]);

    if (event.id) {
      // Update existing event
      apiService
        .updateEvent(event!.id, event)
        .then((response) => {
          console.log("Event updated successfully:", response.data);
          alert("Akce upravena úspěšně");
        })
        .catch((error) => {
          console.error("Error updating event:", error);
          alert("Chyba při úpravě akce");
        });
    } else {
      // Create new event
      apiService
        .createEvent(event)
        .then((response) => {
          console.log("Event created successfully:", response.data);
          alert("Akce vytvořena úspěšně");
        })
        .catch((error) => {
          console.error("Error creating event:", error);
          alert("Chyba při vytváření akce");
        });
    }
  };

  return {
    event,
    setEvent,
    accommodationTypes,
    feeTypes,
    services,
    events,
    loadSpecificEvent,
    handleEventChange,
    handleDatesChange,
    handleServiceChange,
    handleCheckboxChange,
    addService,
    removeService,
    handleAccommodationTypeChange,
    handleFeeTypeChange,
    handleFeeCheckboxChange,
    addAccommodationType,
    addFeeType,
    removeAccommodationType,
    removeFeeType,
    handleFormFieldsChange,
    handleSubmit,
    submittedAccommodationTypes
  };
};
